import React, { useState, useEffect } from 'react'
import {
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  ExpandMore,
  Settings,
  Security,
  CheckCircle,
  Error,
  Search,
  Visibility,
  VisibilityOff,
  ContentCopy,
  PlayArrow,
  CloudUpload,
  VpnKey,
  Refresh,
  Psychology,
  Email,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  Description,
  Cancel,
  Map,
} from '@mui/icons-material'
import { adminAPI, clusterAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'

const AdminEnvironmentPage = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [environmentData, setEnvironmentData] = useState(null)
  const [summaryData, setSummaryData] = useState(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [expandedCategory, setExpandedCategory] = useState('agentflow')
  const [visibleValues, setVisibleValues] = useState(new Set())
  
  // Storage test state
  const [storageTestLoading, setStorageTestLoading] = useState(false)
  const [storageTestResult, setStorageTestResult] = useState(null)
  
  // Azure credentials state
  const [credentialsLoading, setCredentialsLoading] = useState(false)
  const [credentialsData, setCredentialsData] = useState(null)
  
  // LLM test state
  const [llmTestLoading, setLlmTestLoading] = useState(false)
  const [llmTestResult, setLlmTestResult] = useState(null)
  const [availableModels, setAvailableModels] = useState([])
  const [selectedModel, setSelectedModel] = useState('')
  const [modelsLoading, setModelsLoading] = useState(false)

  // Izzi Internal test state
  const [transactionId, setTransactionId] = useState('')
  const [izziInternalTestLoading, setIzziInternalTestLoading] = useState(false)
  const [izziInternalTestResult, setIzziInternalTestResult] = useState(null)

  // Izzi External test state
  const [izziExternalTestLoading, setIzziExternalTestLoading] = useState(false)
  const [izziExternalTestResult, setIzziExternalTestResult] = useState(null)

  // Email test state
  const [emailTestLoading, setEmailTestLoading] = useState(false)
  const [emailTestState, setEmailTestState] = useState(null)
  const [emailTestPolling, setEmailTestPolling] = useState(false)
  const [emailPollerTriggering, setEmailPollerTriggering] = useState(false)
  const emailTestPollingTimeoutRef = React.useRef(null)

  // OCR test state
  const [ocrTestLoading, setOcrTestLoading] = useState(false)
  const [ocrTestResult, setOcrTestResult] = useState(null)

  // Error logging test state
  const [errorTestLoading, setErrorTestLoading] = useState(false)
  const [errorTestResult, setErrorTestResult] = useState(null)

  // Azure Maps test state
  const [azureMapsTestLoading, setAzureMapsTestLoading] = useState(false)
  const [azureMapsTestResult, setAzureMapsTestResult] = useState(null)

  // Google Maps test state
  const [googleMapsTestLoading, setGoogleMapsTestLoading] = useState(false)
  const [googleMapsTestResult, setGoogleMapsTestResult] = useState(null)

  useEffect(() => {
    fetchEnvironmentData()
    fetchEmailTestStatus()  // Load email test status on mount
    fetchAvailableModels()  // Load available LLM models on mount
  }, [])

  const fetchEmailTestStatus = async (autoStartPolling = false) => {
    try {
      const result = await adminAPI.getEmailTestStatus()
      if (result.test_state) {
        // Detect stale tests (elapsed > 180s and still running)
        const isStale = (result.test_state.status === 'waiting' || result.test_state.status === 'sending') &&
                       result.test_state.elapsed_seconds > 180

        if (isStale) {
          // Mark stale test as timeout
          result.test_state.status = 'timeout'
          result.test_state.error_message = 'Test timed out (stale state from previous run)'
        }

        setEmailTestState(result.test_state)

        // Only start polling if explicitly requested (not on page load/refresh)
        if (autoStartPolling && !isStale && (result.test_state.status === 'waiting' || result.test_state.status === 'sending')) {
          startEmailTestPolling()
        }
      }
    } catch (err) {
      console.error('Error fetching email test status:', err)
    }
  }

  const fetchEnvironmentData = async () => {
    try {
      setLoading(true)
      setError('')

      // Fetch both environment variables and summary
      const [envResponse, summaryResponse] = await Promise.all([
        adminAPI.getEnvironmentVariables(),
        adminAPI.getEnvironmentSummary()
      ])

      setEnvironmentData(envResponse)
      setSummaryData(summaryResponse)
    } catch (err) {
      setError(`Failed to load environment data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const testAzureStorage = async () => {
    try {
      setStorageTestLoading(true)
      const result = await adminAPI.testAzureStorage()
      setStorageTestResult(result)
    } catch (err) {
      setStorageTestResult({
        success: false,
        error: `Failed to test storage: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setStorageTestLoading(false)
    }
  }

  const fetchAvailableModels = async () => {
    try {
      setModelsLoading(true)
      const result = await adminAPI.getAvailableLLMModels()
      setAvailableModels(result.models || [])

      // Set default model if available
      if (result.models && result.models.length > 0) {
        // Try to select the default model
        const defaultModel = result.models.find(m => m.is_default)
        if (defaultModel) {
          setSelectedModel(defaultModel.service_id)
        } else if (result.default_model) {
          setSelectedModel(result.default_model)
        } else {
          // Just select the first model
          setSelectedModel(result.models[0].service_id)
        }
      }
    } catch (err) {
      console.error('Error fetching available models:', err)
    } finally {
      setModelsLoading(false)
    }
  }

  const testLLMService = async () => {
    try {
      setLlmTestLoading(true)
      // Pass selected model to the test
      const result = await adminAPI.testLLMService(selectedModel || null)
      setLlmTestResult(result)
    } catch (err) {
      setLlmTestResult({
        success: false,
        error: `Failed to test LLM service: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setLlmTestLoading(false)
    }
  }

  const testIzziInternal = async () => {
    if (!transactionId || transactionId.trim() === '') {
      setIzziInternalTestResult({
        success: false,
        error: 'Please enter a transaction ID',
        timestamp: new Date().toISOString()
      })
      return
    }

    try {
      setIzziInternalTestLoading(true)
      const result = await adminAPI.testIzziInternal(transactionId.trim())
      setIzziInternalTestResult(result)
    } catch (err) {
      setIzziInternalTestResult({
        success: false,
        error: `Failed to test Izzi Internal API: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setIzziInternalTestLoading(false)
    }
  }

  const testIzziExternal = async () => {
    try {
      setIzziExternalTestLoading(true)
      const result = await adminAPI.testIzziExternal()
      setIzziExternalTestResult(result)
    } catch (err) {
      setIzziExternalTestResult({
        success: false,
        error: `Failed to test Izzi External API: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setIzziExternalTestLoading(false)
    }
  }

  const fetchAzureCredentials = async () => {
    try {
      setCredentialsLoading(true)
      const result = await adminAPI.getAzureCredentialInfo()
      setCredentialsData(result)
    } catch (err) {
      setCredentialsData({
        error: `Failed to fetch credential info: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setCredentialsLoading(false)
    }
  }

  const testOCRService = async () => {
    try {
      setOcrTestLoading(true)
      const result = await adminAPI.testOCRService()
      setOcrTestResult(result)
    } catch (err) {
      setOcrTestResult({
        success: false,
        error: `Failed to test OCR service: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setOcrTestLoading(false)
    }
  }

  const testErrorLogging = async () => {
    try {
      setErrorTestLoading(true)
      const result = await adminAPI.testErrorLogging()
      setErrorTestResult(result)
    } catch (err) {
      setErrorTestResult({
        success: false,
        error: `Failed to test error logging: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setErrorTestLoading(false)
    }
  }

  const testAzureMaps = async () => {
    try {
      setAzureMapsTestLoading(true)
      const result = await adminAPI.testAzureMaps()
      setAzureMapsTestResult(result)
    } catch (err) {
      setAzureMapsTestResult({
        success: false,
        error: `Failed to test Azure Maps: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setAzureMapsTestLoading(false)
    }
  }

  const testGoogleMaps = async () => {
    try {
      setGoogleMapsTestLoading(true)
      const result = await adminAPI.testGoogleMaps()
      setGoogleMapsTestResult(result)
    } catch (err) {
      setGoogleMapsTestResult({
        success: false,
        error: `Failed to test Google Maps: ${err.message}`,
        timestamp: new Date().toISOString()
      })
    } finally {
      setGoogleMapsTestLoading(false)
    }
  }

  const sendTestEmail = async () => {
    try {
      setEmailTestLoading(true)
      setEmailTestState(null)

      const result = await adminAPI.sendTestEmail()

      setEmailTestState(result.test_state)

      // Start polling if email was sent successfully
      if (result.success && result.test_state && result.test_state.status === 'waiting') {
        startEmailTestPolling()
      }
    } catch (err) {
      console.error('Error sending test email:', err)
      setEmailTestState({
        status: 'failed',
        error_message: `Failed to send test email: ${err.message}`
      })
    } finally {
      setEmailTestLoading(false)
    }
  }

  const startEmailTestPolling = () => {
    if (emailTestPolling) {
      return
    }

    setEmailTestPolling(true)
    pollEmailTestStatus()
  }

  const pollEmailTestStatus = async () => {
    try {
      const result = await adminAPI.getEmailTestStatus()

      if (result.test_state) {
        // Detect stale tests (elapsed > 180s and still running)
        const isStale = (result.test_state.status === 'waiting' || result.test_state.status === 'sending') &&
                       result.test_state.elapsed_seconds > 180

        if (isStale) {
          // Mark stale test as timeout
          result.test_state.status = 'timeout'
          result.test_state.error_message = 'Test timed out'
          setEmailTestState(result.test_state)
          setEmailTestPolling(false)
          return
        }

        setEmailTestState(result.test_state)

        // Continue polling if still waiting
        if (result.test_state.status === 'waiting' || result.test_state.status === 'sending') {
          emailTestPollingTimeoutRef.current = setTimeout(pollEmailTestStatus, 2000) // Poll every 2 seconds
        } else {
          // Test completed (success, failed, or timeout)
          setEmailTestPolling(false)
        }
      } else {
        setEmailTestPolling(false)
      }
    } catch (err) {
      console.error('Error polling email test status:', err)
      setEmailTestPolling(false)
    }
  }

  const triggerEmailPoller = async () => {
    try {
      setEmailPollerTriggering(true)
      await clusterAPI.triggerJob('email_polling')

      // Refresh email test status after triggering
      await fetchEmailTestStatus()
    } catch (err) {
      console.error('Error triggering email poller:', err)
    } finally {
      setEmailPollerTriggering(false)
    }
  }

  const cancelEmailTest = async () => {
    // Clear any scheduled polling timeout
    if (emailTestPollingTimeoutRef.current) {
      clearTimeout(emailTestPollingTimeoutRef.current)
      emailTestPollingTimeoutRef.current = null
    }

    // Clear local state immediately
    setEmailTestPolling(false)
    setEmailTestState(null)
    setEmailTestLoading(false)

    // Call API to clear server-side state
    try {
      await adminAPI.cancelEmailTest()
    } catch (err) {
      console.error('Error cancelling email test on server:', err)
    }
  }

  const toggleValueVisibility = (key) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  const isValueMasked = (value) => {
    return value && (value.includes('***masked***') || value.includes('*'))
  }

  const getConfigurationStatus = (configured) => {
    return configured ? (
      <Chip 
        icon={<CheckCircle />} 
        label="Configured" 
        color="success" 
        size="small" 
      />
    ) : (
      <Chip 
        icon={<Error />} 
        label="Not Set" 
        color="error" 
        size="small" 
      />
    )
  }

  const filterVariables = (variables) => {
    if (!searchFilter) return variables
    
    const filtered = {}
    Object.entries(variables).forEach(([key, value]) => {
      if (key.toLowerCase().includes(searchFilter.toLowerCase()) ||
          (typeof value === 'string' && value.toLowerCase().includes(searchFilter.toLowerCase()))) {
        filtered[key] = value
      }
    })
    return filtered
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'agentflow': return <Settings />
      case 'azure': return <Security />
      case 'monitoring': return <Psychology />
      default: return <Settings />
    }
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'agentflow': return 'primary'
      case 'azure': return 'secondary'
      case 'integrations': return 'info'
      case 'email': return 'success'
      case 'monitoring': return 'warning'
      default: return 'default'
    }
  }

  if (loading) {
    return (
      <AdminPageWrapper title="Environment Configuration">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminPageWrapper>
    )
  }

  if (error) {
    return (
      <AdminPageWrapper title="Environment Configuration">
        <Alert severity="error">{error}</Alert>
      </AdminPageWrapper>
    )
  }

  return (
    <AdminPageWrapper
      title="Environment Configuration"
      subtitle="View application environment variables and configuration status. Sensitive values are masked for security."
    >

      {/* Configuration Summary */}
      {summaryData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Configuration Summary
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Environment
                  </Typography>
                  <Typography variant="h6">
                    {summaryData.environment}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Region
                  </Typography>
                  <Typography variant="h6">
                    {summaryData.region}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Database Type
                  </Typography>
                  <Typography variant="h6">
                    {summaryData.database_type}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Auto Processing
                  </Typography>
                  <Typography variant="h6">
                    {summaryData.auto_processing_enabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Critical Configuration Status */}
          {Object.entries(summaryData.configuration_summary)
            .filter(([category]) => category !== 'Azure OpenAI')  // Exclude obsolete Azure OpenAI section
            .map(([category, configs]) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {category}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Configuration</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(configs).map(([key, config]) => (
                      <TableRow key={key}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {key}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getConfigurationStatus(config.configured)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {config.value || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Paper>
      )}

      {/* Azure Tools */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Azure Configuration Tools
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Test Azure Storage connectivity and view credential information.
        </Typography>

        <Grid container spacing={2}>
          {/* Storage Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CloudUpload color="primary" />
                  <Typography variant="h6">
                    Storage Test
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Test Azure Storage by writing and reading a small test file.
                </Typography>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton 
                    onClick={testAzureStorage}
                    disabled={storageTestLoading}
                    color="primary"
                  >
                    {storageTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {storageTestLoading ? 'Testing...' : 'Run Storage Test'}
                  </Typography>
                </Box>

                {/* Storage Test Results */}
                {storageTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={storageTestResult.success ? 'success' : storageTestResult.warning ? 'warning' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {storageTestResult.success && (
                        <Typography variant="body2">Storage test completed successfully!</Typography>
                      )}
                      {storageTestResult.warning && (
                        <Typography variant="body2">{storageTestResult.warning}</Typography>
                      )}
                      {storageTestResult.error && (
                        <Typography variant="body2">{storageTestResult.error}</Typography>
                      )}
                    </Alert>
                    
                    {storageTestResult.test_details && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <pre style={{ fontSize: '0.8rem', overflow: 'auto' }}>
                            {JSON.stringify(storageTestResult.test_details, null, 2)}
                          </pre>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* LLM Service Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Psychology color="primary" />
                  <Typography variant="h6">
                    LLM Service Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test LLM model configuration by making a real chat completion call.
                </Typography>

                {/* Model Selection Dropdown */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="llm-model-select-label">Select Model</InputLabel>
                  <Select
                    labelId="llm-model-select-label"
                    id="llm-model-select"
                    value={selectedModel}
                    label="Select Model"
                    onChange={(e) => setSelectedModel(e.target.value)}
                    disabled={modelsLoading || availableModels.length === 0}
                  >
                    {availableModels.map((model) => {
                      // Check if OpenAI model without API key
                      const isOpenAI = model.provider.toLowerCase() === 'openai'
                      const hasApiKey = model.configuration?.api_key_configured
                      const isDisabled = isOpenAI && !hasApiKey

                      return (
                        <MenuItem
                          key={model.service_id}
                          value={model.service_id}
                          disabled={isDisabled}
                          sx={{
                            opacity: isDisabled ? 0.5 : 1,
                            '&.Mui-disabled': {
                              opacity: 0.5
                            }
                          }}
                        >
                          <Box>
                            <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.primary'}>
                              {model.name}
                              {model.is_default && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  color="primary"
                                  sx={{ ml: 1, height: 20 }}
                                />
                              )}
                              {isDisabled && (
                                <Chip
                                  label="API Key Missing"
                                  size="small"
                                  color="error"
                                  sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" color={isDisabled ? 'text.disabled' : 'text.secondary'}>
                              {model.provider} • {model.model_name}
                            </Typography>
                          </Box>
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={testLLMService}
                    disabled={llmTestLoading || !selectedModel}
                    color="primary"
                  >
                    {llmTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {llmTestLoading ? 'Testing...' : 'Run LLM Test'}
                  </Typography>
                </Box>

                {/* LLM Test Results */}
                {llmTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={llmTestResult.success ? 'success' : llmTestResult.warning ? 'warning' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {llmTestResult.success && (
                        <Typography variant="body2">LLM service test completed successfully!</Typography>
                      )}
                      {llmTestResult.warning && (
                        <Typography variant="body2">{llmTestResult.warning}</Typography>
                      )}
                      {llmTestResult.error && (
                        <Typography variant="body2">{llmTestResult.error}</Typography>
                      )}
                    </Alert>
                    
                    {llmTestResult.test_details && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {/* Configuration Section */}
                          {llmTestResult.configuration && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" gutterBottom>
                                Configuration:
                              </Typography>
                              <pre style={{ fontSize: '0.8rem', overflow: 'auto', marginBottom: '1rem' }}>
                                {JSON.stringify(llmTestResult.configuration, null, 2)}
                              </pre>
                            </Box>
                          )}
                          
                          {/* LLM Response */}
                          {llmTestResult.success && llmTestResult.test_details.response_content && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" gutterBottom>
                                LLM Response:
                              </Typography>
                              <Alert severity="info" sx={{ mb: 1 }}>
                                "{llmTestResult.test_details.response_content}"
                              </Alert>
                              <Typography variant="caption" color="text.secondary">
                                Execution time: {llmTestResult.test_details.execution_time_ms}ms
                                {llmTestResult.test_details.model_used && ` • Model: ${llmTestResult.test_details.model_used}`}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Full Details */}
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="caption">Full Test Details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <pre style={{ fontSize: '0.7rem', overflow: 'auto' }}>
                                {JSON.stringify(llmTestResult.test_details, null, 2)}
                              </pre>
                            </AccordionDetails>
                          </Accordion>
                        </AccordionDetails>
                      </Accordion>
                    )}
                    
                    {/* Recommendations */}
                    {llmTestResult.recommendations && llmTestResult.recommendations.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Recommendations:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                          {llmTestResult.recommendations.map((rec, idx) => (
                            <li key={idx} style={{ fontSize: '0.8rem' }}>{rec}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Izzi Internal Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Search color="primary" />
                  <Typography variant="h6">
                    Izzi Internal Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test Izzi Internal API by fetching invoice data for a transaction ID.
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    size="small"
                    placeholder="Enter transaction ID"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        testIzziInternal()
                      }
                    }}
                    disabled={izziInternalTestLoading}
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    onClick={testIzziInternal}
                    disabled={izziInternalTestLoading || !transactionId}
                    color="primary"
                  >
                    {izziInternalTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                </Box>

                {/* Izzi Internal Test Results */}
                {izziInternalTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={izziInternalTestResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {izziInternalTestResult.success && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Document Number: {izziInternalTestResult.document_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Transaction ID: {izziInternalTestResult.transaction_id}
                          </Typography>
                        </>
                      )}
                      {izziInternalTestResult.error && (
                        <Typography variant="body2">
                          {izziInternalTestResult.error}
                        </Typography>
                      )}
                    </Alert>

                    {izziInternalTestResult.test_details && Object.keys(izziInternalTestResult.test_details).length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                {Object.entries(izziInternalTestResult.test_details).map(([key, value]) => (
                                  <TableRow key={key}>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {key}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {value !== null && value !== undefined ? String(value) : 'null'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Izzi External Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Search color="secondary" />
                  <Typography variant="h6">
                    Izzi External Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test Izzi External API by fetching dispute reason codes.
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={testIzziExternal}
                    disabled={izziExternalTestLoading}
                    color="secondary"
                  >
                    {izziExternalTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {izziExternalTestLoading ? 'Testing...' : 'Run Izzi External Test'}
                  </Typography>
                </Box>

                {/* Izzi External Test Results */}
                {izziExternalTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={izziExternalTestResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {izziExternalTestResult.success && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Dispute Codes Retrieved: {izziExternalTestResult.dispute_codes_count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Izzi External API connection successful
                          </Typography>
                        </>
                      )}
                      {izziExternalTestResult.error && (
                        <Typography variant="body2">
                          {izziExternalTestResult.error}
                        </Typography>
                      )}
                    </Alert>

                    {izziExternalTestResult.test_details && Object.keys(izziExternalTestResult.test_details).length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      execution_time_ms
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {izziExternalTestResult.test_details.execution_time_ms}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      total_codes
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {izziExternalTestResult.test_details.total_codes}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {izziExternalTestResult.test_details.sample_codes && izziExternalTestResult.test_details.sample_codes.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="subtitle2" gutterBottom>
                                Sample Dispute Codes:
                              </Typography>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Code</TableCell>
                                      <TableCell>Description</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {izziExternalTestResult.test_details.sample_codes.map((code, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <Typography variant="body2" fontFamily="monospace">
                                            {code.code}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {code.description || '-'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Azure Credentials Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <VpnKey color="secondary" />
                  <Typography variant="h6">
                    Azure Credentials
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  View Azure DefaultCredential configuration and authentication methods.
                </Typography>
                
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton 
                    onClick={fetchAzureCredentials}
                    disabled={credentialsLoading}
                    color="secondary"
                  >
                    {credentialsLoading ? <CircularProgress size={24} /> : <Refresh />}
                  </IconButton>
                  <Typography variant="body2">
                    {credentialsLoading ? 'Loading...' : 'Check Credentials'}
                  </Typography>
                </Box>

                {/* Azure Credentials Results */}
                {credentialsData && (
                  <Box mt={2}>
                    {credentialsData.error ? (
                      <Alert severity="error">
                        {credentialsData.error}
                      </Alert>
                    ) : (
                      <>
                        {/* Warnings */}
                        {credentialsData.warnings && credentialsData.warnings.length > 0 && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            {credentialsData.warnings.join('; ')}
                          </Alert>
                        )}
                        
                        {/* Recommendations */}
                        {credentialsData.recommendations && credentialsData.recommendations.length > 0 && (
                          <Alert severity="info" sx={{ mb: 1 }}>
                            {credentialsData.recommendations.join('; ')}
                          </Alert>
                        )}
                        
                        {/* Overall Status */}
                        {credentialsData.working_credential && (
                          <Alert severity="success" sx={{ mb: 2 }}>
                            ✓ Working authentication method: {credentialsData.working_credential}
                          </Alert>
                        )}
                        
                        {credentialsData.default_credential_working === true && (
                          <Alert severity="success" sx={{ mb: 2 }}>
                            ✓ DefaultAzureCredential is working correctly
                          </Alert>
                        )}
                        
                        {credentialsData.default_credential_working === false && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            ⚠ DefaultAzureCredential failed, but individual credentials may work
                            {credentialsData.default_credential_error && (
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                Error: {credentialsData.default_credential_error}
                              </Typography>
                            )}
                          </Alert>
                        )}

                        {/* Credential Chain */}
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle2">
                              Credential Chain ({credentialsData.credential_chain?.length || 0} methods)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Priority</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Test Result</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {credentialsData.credential_chain?.map((cred, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{cred.priority}</TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                          {cred.type}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={cred.status} 
                                          size="small"
                                          color={
                                            cred.status === 'working' ? 'success' :
                                            cred.status === 'available' ? 'info' :
                                            cred.status === 'partial' ? 'warning' : 
                                            cred.status === 'failed' ? 'error' : 'default'
                                          }
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {cred.description}
                                          {cred.note && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                              {cred.note}
                                            </Typography>
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {cred.actual_test ? (
                                          <Typography 
                                            variant="body2" 
                                            color={cred.status === 'working' ? 'success.main' : 'error.main'}
                                            fontFamily="monospace"
                                          >
                                            {cred.actual_test}
                                            {cred.token_expires && (
                                              <Typography variant="caption" display="block" color="text.secondary">
                                                Expires: {new Date(cred.token_expires).toLocaleString()}
                                              </Typography>
                                            )}
                                          </Typography>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            Not tested
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                        
                        {/* Environment Variables */}
                        <Accordion sx={{ mt: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle2">Environment Variables</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <TableContainer>
                              <Table size="small">
                                <TableBody>
                                  {Object.entries(credentialsData.environment_variables || {}).map(([key, value]) => (
                                    <TableRow key={key}>
                                      <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                          {key}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                          {value || '<not set>'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Email Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignments="center" gap={1} mb={2}>
                  <Email color="primary" />
                  <Typography variant="h6">
                    Email Configuration Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Send a test email and verify email polling is working.
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={sendTestEmail}
                    disabled={emailTestLoading || emailTestPolling}
                    color="primary"
                  >
                    {emailTestLoading || emailTestPolling ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {emailTestLoading ? 'Sending email...' : emailTestPolling ? 'Waiting for email...' : 'Send Test Email'}
                  </Typography>
                  {(emailTestLoading || emailTestPolling) && (
                    <Button
                      size="small"
                      color="error"
                      onClick={cancelEmailTest}
                      sx={{ ml: 'auto' }}
                    >
                      Cancel
                    </Button>
                  )}
                </Box>

                {/* Email Test Status */}
                {emailTestState && (
                  <Box mt={2}>
                    {/* Show badge if this is a previous test result (not currently running) */}
                    {emailTestState.status !== 'waiting' && emailTestState.status !== 'sending' && (
                      <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                        <Typography variant="caption">
                          Last test result from {new Date(emailTestState.completed_at || emailTestState.started_at).toLocaleString()}
                        </Typography>
                      </Alert>
                    )}

                    {/* Status Alert */}
                    {emailTestState.status === 'waiting' && (
                      <>
                        <Alert severity="info" icon={<HourglassEmpty />} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Waiting for email to arrive...
                          </Typography>
                          <Typography variant="body2">
                            Elapsed time: {emailTestState.elapsed_seconds}s / 180s
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Test ID: {emailTestState.test_id}
                          </Typography>
                        </Alert>

                        <Alert
                          severity="warning"
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="subtitle2" gutterBottom>
                            📧 Please remember to trigger Email Poller
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            The email polling service checks for new emails at configured intervals.
                            To speed up detection, manually trigger the email poller.
                          </Typography>
                          <Box display="flex" gap={1} mt={2}>
                            <Button
                              color="inherit"
                              size="small"
                              variant="outlined"
                              startIcon={<PlayArrow />}
                              onClick={triggerEmailPoller}
                              disabled={emailPollerTriggering}
                            >
                              {emailPollerTriggering ? 'Triggering...' : 'Trigger Now'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={cancelEmailTest}
                              disabled={emailTestLoading}
                            >
                              Cancel Test
                            </Button>
                          </Box>
                        </Alert>
                      </>
                    )}

                    {emailTestState.status === 'success' && (
                      <Alert severity="success" icon={<CheckCircleOutline />} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          ✓ Email test completed successfully!
                        </Typography>
                        <Typography variant="body2">
                          Email was sent and received in {emailTestState.elapsed_seconds}s
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Recipient: {emailTestState.recipient_email}
                        </Typography>
                      </Alert>
                    )}

                    {emailTestState.status === 'timeout' && (
                      <Alert
                        severity="warning"
                        icon={<ErrorOutline />}
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          Email test timed out
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {emailTestState.error_message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          The email may have been sent but was not received within 3 minutes.
                          Try triggering the email poller manually or check your SMTP settings.
                        </Typography>
                        <Box display="flex" gap={1} mt={2}>
                          <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            startIcon={<PlayArrow />}
                            onClick={triggerEmailPoller}
                            disabled={emailPollerTriggering}
                          >
                            {emailPollerTriggering ? 'Triggering...' : 'Trigger Poller'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={cancelEmailTest}
                            disabled={emailTestLoading}
                          >
                            Cancel Test
                          </Button>
                        </Box>
                      </Alert>
                    )}

                    {emailTestState.status === 'failed' && (
                      <Alert severity="error" icon={<Error />} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Email test failed
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {emailTestState.error_message}
                        </Typography>
                        <Box display="flex" gap={1} mt={2}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={cancelEmailTest}
                            disabled={emailTestLoading}
                          >
                            Cancel Test
                          </Button>
                        </Box>
                      </Alert>
                    )}

                    {/* Test Details */}
                    {(emailTestState.status === 'success' || emailTestState.status === 'timeout') && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      Test ID
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {emailTestState.test_id}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      Status
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={emailTestState.status}
                                      size="small"
                                      color={
                                        emailTestState.status === 'success' ? 'success' :
                                        emailTestState.status === 'timeout' ? 'warning' : 'error'
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      Elapsed Time
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {emailTestState.elapsed_seconds}s
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                {emailTestState.started_at && (
                                  <TableRow>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        Started At
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {new Date(emailTestState.started_at).toLocaleString()}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {emailTestState.completed_at && (
                                  <TableRow>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        Completed At
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {new Date(emailTestState.completed_at).toLocaleString()}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* OCR Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Description color="primary" />
                  <Typography variant="h6">
                    OCR Service Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test DocumentAI OCR by processing test-pdfs/test-run.pdf.
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={testOCRService}
                    disabled={ocrTestLoading}
                    color="primary"
                  >
                    {ocrTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {ocrTestLoading ? 'Processing PDF...' : 'Run OCR Test'}
                  </Typography>
                </Box>

                {/* OCR Test Results */}
                {ocrTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={ocrTestResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {ocrTestResult.success && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            ✓ OCR processing successful!
                          </Typography>
                          <Typography variant="body2">
                            Document Number: {ocrTestResult.document_number}
                          </Typography>
                        </>
                      )}
                      {ocrTestResult.error && (
                        <Typography variant="body2">
                          {ocrTestResult.error}
                        </Typography>
                      )}
                    </Alert>

                    {ocrTestResult.test_details && Object.keys(ocrTestResult.test_details).length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                {Object.entries(ocrTestResult.test_details).map(([key, value]) => (
                                  <TableRow key={key}>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {key}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {value !== null && value !== undefined ? String(value) : 'null'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Error Logging Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ErrorOutline color="error" />
                  <Typography variant="h6">
                    Application Insights Error Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test error logging to Azure Application Insights. Triggers a test error that should appear in App Insights.
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={testErrorLogging}
                    disabled={errorTestLoading}
                    color="error"
                  >
                    {errorTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {errorTestLoading ? 'Logging test error...' : 'Trigger Test Error'}
                  </Typography>
                </Box>

                {/* Error Logging Test Results */}
                {errorTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={errorTestResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {errorTestResult.message && (
                        <Typography variant="body2">
                          {errorTestResult.message}
                        </Typography>
                      )}
                      {errorTestResult.error && (
                        <Typography variant="body2">
                          {errorTestResult.error}
                        </Typography>
                      )}
                    </Alert>

                    {errorTestResult.test_id && (
                      <Box mt={2} p={2} sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                          Test Error ID (search for this in App Insights):
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                          {errorTestResult.test_id}
                        </Typography>
                      </Box>
                    )}

                    {errorTestResult.app_insights_configured !== undefined && (
                      <Box mt={2}>
                        <Chip
                          icon={errorTestResult.app_insights_configured ? <CheckCircleOutline /> : <ErrorOutline />}
                          label={errorTestResult.app_insights_configured ? 'App Insights Configured' : 'App Insights NOT Configured'}
                          color={errorTestResult.app_insights_configured ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Azure Maps Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Map color="primary" />
                  <Typography variant="h6">
                    Azure Maps Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test Azure Maps geocoding with "1 andringa street stellenbosch".
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={testAzureMaps}
                    disabled={azureMapsTestLoading}
                    color="primary"
                  >
                    {azureMapsTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {azureMapsTestLoading ? 'Testing...' : 'Run Azure Maps Test'}
                  </Typography>
                </Box>

                {/* Azure Maps Test Results */}
                {azureMapsTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={azureMapsTestResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {azureMapsTestResult.success && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            ✓ Geocoding successful!
                          </Typography>
                          <Typography variant="body2">
                            {azureMapsTestResult.formatted_address}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Lat: {azureMapsTestResult.latitude}, Lng: {azureMapsTestResult.longitude}
                          </Typography>
                        </>
                      )}
                      {azureMapsTestResult.error && (
                        <Typography variant="body2">
                          {azureMapsTestResult.error}
                        </Typography>
                      )}
                    </Alert>

                    {azureMapsTestResult.test_details && Object.keys(azureMapsTestResult.test_details).length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <pre style={{ fontSize: '0.8rem', overflow: 'auto' }}>
                            {JSON.stringify(azureMapsTestResult.test_details, null, 2)}
                          </pre>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Google Maps Test Tool */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Map color="success" />
                  <Typography variant="h6">
                    Google Maps Test
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Test Google Maps geocoding with "1 andringa street stellenbosch".
                </Typography>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <IconButton
                    onClick={testGoogleMaps}
                    disabled={googleMapsTestLoading}
                    color="success"
                  >
                    {googleMapsTestLoading ? <CircularProgress size={24} /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2">
                    {googleMapsTestLoading ? 'Testing...' : 'Run Google Maps Test'}
                  </Typography>
                </Box>

                {/* Google Maps Test Results */}
                {googleMapsTestResult && (
                  <Box mt={2}>
                    <Alert
                      severity={googleMapsTestResult.success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {googleMapsTestResult.success && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            ✓ Geocoding successful!
                          </Typography>
                          <Typography variant="body2">
                            {googleMapsTestResult.formatted_address}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Lat: {googleMapsTestResult.latitude}, Lng: {googleMapsTestResult.longitude}
                          </Typography>
                        </>
                      )}
                      {googleMapsTestResult.error && (
                        <Typography variant="body2">
                          {googleMapsTestResult.error}
                        </Typography>
                      )}
                    </Alert>

                    {googleMapsTestResult.test_details && Object.keys(googleMapsTestResult.test_details).length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <pre style={{ fontSize: '0.8rem', overflow: 'auto' }}>
                            {JSON.stringify(googleMapsTestResult.test_details, null, 2)}
                          </pre>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Environment Variables */}
      {environmentData && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Environment Variables ({environmentData.total_variables} total)
            </Typography>
            <TextField
              size="small"
              placeholder="Search variables..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {environmentData.masking_info}
          </Alert>

          {Object.entries(environmentData.categories).map(([category, variables]) => {
            const filteredVars = filterVariables(variables)
            const varCount = Object.keys(filteredVars).length
            
            if (varCount === 0) return null

            return (
              <Accordion 
                key={category}
                expanded={expandedCategory === category}
                onChange={() => setExpandedCategory(expandedCategory === category ? '' : category)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getCategoryIcon(category)}
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {category}
                    </Typography>
                    <Chip 
                      label={`${varCount} variables`} 
                      size="small" 
                      color={getCategoryColor(category)}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Variable</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell width={100}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(filteredVars).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                {key}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                fontFamily="monospace"
                                sx={{
                                  wordBreak: 'break-all',
                                  filter: !visibleValues.has(key) && isValueMasked(value) ? 'blur(4px)' : 'none'
                                }}
                              >
                                {value || '<not set>'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={1}>
                                {isValueMasked(value) && (
                                  <Tooltip title={visibleValues.has(key) ? "Hide value" : "Show value"}>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => toggleValueVisibility(key)}
                                    >
                                      {visibleValues.has(key) ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {value && (
                                  <Tooltip title="Copy to clipboard">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => copyToClipboard(value)}
                                    >
                                      <ContentCopy />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Paper>
      )}
    </AdminPageWrapper>
  )
}

export default AdminEnvironmentPage